import { useMutation, useQuery } from 'react-query'
import APIService from 'api/APIService'
import { downloadFile } from 'utils/AppHelper'
import { JobStatus, MergePreviewManagersPayload } from 'types/entity'
import { useSettings } from 'core/hooks/useSettings'
import useNotifications from 'core/hooks/useNotifications'
import useCurrentTeam from 'core/hooks/useCurrentTeam'

interface Props {
  onSuccess?: VoidFunction
  groupIntegrationId?: MergePreviewManagersPayload['groupIntegrationId']
}

const useDownloadMergeManagers = ({ onSuccess, groupIntegrationId }: Props) => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()
  const { data: currentTeam } = useCurrentTeam()

  const jobID = settings.pollingJobs.downloadMergeManagersJobId

  const {
    mutate: downloadManagers,
    status: downloadManagersStatus,
    isLoading: downloadManagersLoading
  } = useMutation({
    mutationFn: ({
      teamAssignmentStrategy,
      remoteFieldTraversalPath,
      remoteUrlPath
    }: {
      teamAssignmentStrategy: MergePreviewManagersPayload['team_assignment_strategy']
      remoteFieldTraversalPath?: MergePreviewManagersPayload['remote_field_traversal_path']
      remoteUrlPath?: MergePreviewManagersPayload['remote_url_path']
    }) =>
      APIService.integrations.downloadManagers({
        teamId: currentTeam?.token_id ?? '',
        groupIntegrationId: groupIntegrationId ?? '',
        team_assignment_strategy: teamAssignmentStrategy,
        remote_field_traversal_path: remoteFieldTraversalPath,
        remote_url_path: remoteUrlPath
      }),
    onSuccess: (data) => {
      set('pollingJobs', {
        ...settings.pollingJobs,
        downloadMergeManagersJobId: data.token_id
      })
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  const loadingNotificationId = `download-merge-managers-${jobID}`

  useQuery(
    ['download-merge-managers', jobID],
    () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    {
      enabled: !!jobID,
      refetchInterval: 2000,
      onSuccess: (response: JobStatus) => {
        const errors = response?.error_list ?? []
        const hasErrors = errors.length > 0
        if (response.status === 'finished' && !hasErrors) {
          set('pollingJobs', {
            ...settings.pollingJobs,
            downloadMergeManagersJobId: ''
          })
          downloadFile(response.data.file_url as string)
          notification.success({
            id: jobID,
            title: 'Your download is complete'
          })
          onSuccess?.()
          notification.dismiss(loadingNotificationId)
        }

        if (
          (response.status === 'in_progress' ||
            response.status === 'enqueued') &&
          !hasErrors
        ) {
          notification.loading({
            id: loadingNotificationId,
            position: 'bottom-left',
            title: 'Exporting...',
            subtitle:
              'The download starts when the export is complete. This may take sometime',
            duration: Infinity
          })
        }

        if (
          response.status === 'failed' ||
          (response.status === 'finished' && hasErrors)
        ) {
          notification.dismiss(loadingNotificationId)
          notification.error({
            title: 'An error has occurred trying to download this file',
            id: jobID
          })
          set('pollingJobs', {
            ...settings.pollingJobs,
            downloadMergeManagersJobId: ''
          })
        }
      },
      onError: () => {
        notification.dismiss(loadingNotificationId)
        notification.error({
          title: 'Could not download merge managers',
          id: jobID
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          downloadMergeManagersJobId: ''
        })
      }
    }
  )

  return {
    downloadManagers,
    downloadManagersStatus,
    downloadManagersLoading
  }
}

export default useDownloadMergeManagers
